import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import type {
  PerformanceMetric,
  PerformanceProcessBucket,
} from "@/shared/types/production";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  formatMetricDisplay,
  metricDisplayLabel,
  metricValue,
} from "@/frontend/lib/production-stats";

interface Props {
  processes: PerformanceProcessBucket[];
  metric: PerformanceMetric;
}

export default function PerformanceSmallMultiples({ processes, metric }: Props) {
  const barColor = metric === "good_units" ? "#14b8a6" : "#8b5cf6";
  const metricLabel = metricDisplayLabel(metric);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {processes.map((proc) => {
        const chartData = [...proc.buckets]
          .sort((a, b) => metricValue(b, metric) - metricValue(a, metric))
          .map((b) => ({
            name: b.employeeName,
            value: metricValue(b, metric),
          }));

        return (
          <Card key={proc.processTitle}>
            <CardHeader className="pb-2">
              <CardTitle
                className="text-sm font-medium truncate"
                title={proc.processTitle}
              >
                {proc.processTitle}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2 pt-0">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={chartData}
                  margin={{ top: 4, right: 8, left: 0, bottom: 40 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) =>
                      formatMetricDisplay(Number(value), metric)
                    }
                    width={36}
                  />
                  <Tooltip
                    separator=""
                    labelFormatter={(label) => String(label)}
                    formatter={(value) =>
                      `${metricLabel}: ${formatMetricDisplay(Number(value), metric)}`
                    }
                  />
                  <Bar
                    dataKey="value"
                    name=""
                    fill={barColor}
                    radius={[3, 3, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
